import { getToken } from './auth'

const API_URL = (import.meta as any).env.VITE_API_URL ?? ''

export interface School {
    id: number
    name: string
    username: string
    firebaseUrl: string
    subscriptionExpires?: string
    isActive: boolean
    googleEmails?: string
}

export interface CreateSchoolData {
    name: string
    username: string
    firebaseUrl: string
    password?: string
    subscriptionExpires?: string
    googleEmails?: string
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = getToken()
    if (!token) throw new Error('No token found')

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Request failed')
    }

    return response.json()
}

export async function getSchools(): Promise<School[]> {
    return fetchWithAuth('/api/admin/schools')
}

export async function createSchool(data: CreateSchoolData): Promise<{ id: number, message: string }> {
    return fetchWithAuth('/api/admin/schools', {
        method: 'POST',
        body: JSON.stringify(data)
    })
}

export async function deleteSchool(id: number): Promise<void> {
    return fetchWithAuth(`/api/admin/schools/${id}`, {
        method: 'DELETE'
    })
}

export async function updateSchool(id: number, data: Partial<CreateSchoolData>): Promise<void> {
    return fetchWithAuth(`/api/admin/schools/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    })
}

export interface DatabaseInfo {
    kind: 'server' | 'firebase'
    name: string
    firebaseUrl?: string
    boats: number
    records: number
    estBytes: number
}

export async function getDatabases(): Promise<DatabaseInfo[]> {
    return fetchWithAuth('/api/admin/databases')
}

// ── Per-user boat allowlist (shared store, same as sailgame) ──
export interface AclResult { userKey: string; boats: string[]; restricted: boolean }
export async function getAcl(userKey: string): Promise<AclResult> {
    return fetchWithAuth(`/api/admin/acl/${encodeURIComponent(userKey)}`)
}
export async function setAcl(userKey: string, boats: string[]): Promise<AclResult> {
    return fetchWithAuth(`/api/admin/acl/${encodeURIComponent(userKey)}`, { method: 'PUT', body: JSON.stringify({ boats }) })
}
// Candidate boats for the picker = the own-server (Timescale) distinct boat list.
export async function getServerBoats(): Promise<string[]> {
    try {
        const r = await fetch(`${API_URL}/api/server-boats`)
        return r.ok ? r.json() : []
    } catch { return [] }
}

export async function clearDatabase(target: 'server' | 'firebase', firebaseUrl?: string): Promise<{ ok: boolean, cleared: number }> {
    return fetchWithAuth('/api/admin/clear', {
        method: 'POST',
        body: JSON.stringify({ target, firebaseUrl, boatIds: 'all' })
    })
}
